import React, { FC, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const TAB_LABELS = ['Kommentar', 'Portfolio', 'Config'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`portfolio-detail-tabpanel-${index}`}
      aria-labelledby={`portfolio-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

type PortfolioWidgetItem = {
  sectionTitle?: string;
  widgets?: {
    title?: string;
    art?: string;
    sortierung?: string;
    labelMaxChars?: number;
    valueMaxChars?: number;
    controlValueOn?: number;
    lastRowForCashOrHeader?: number;
    rows?: { label: string; value: string; labelL?: string }[];
  }[];
};

type ThemeData = {
  pieCharts?: { fund: string; colors: string[] }[];
  barCharts?: { fund: string; colors: string[] }[];
  fsHeader?: { fund: string; colors: string[] }[];
};

interface PortfolioUploadTabsProps {
  uploadedData: {
    portfolioWidgets?: PortfolioWidgetItem[];
    theme?: ThemeData;
    monatskommentar?: string | null;
    /** Raw parsed workbook — used as fallback for older uploads without `monatskommentar`. */
    data?: {
      sheets?: Array<{ index?: number; data?: any[][] }>;
      monatskommentar?: string | null;
    } | null;
  } | null;
}

/** Excel Kommentar sheet A5 (0-based row 4, col 0), including older uploads that only stored raw sheets. */
function resolveMonatskommentar(uploadedData: PortfolioUploadTabsProps['uploadedData']): string {
  const direct = String(uploadedData?.monatskommentar || uploadedData?.data?.monatskommentar || '').trim();
  if (direct) return direct;

  const sheets = uploadedData?.data?.sheets;
  if (!Array.isArray(sheets) || sheets.length === 0) return '';
  const kommentarSheet =
    sheets.find((s) => Number(s?.index) === 0) || sheets[0];
  const rows = kommentarSheet?.data;
  if (!Array.isArray(rows) || !Array.isArray(rows[4])) return '';
  const cell = rows[4][0];
  if (cell == null) return '';
  const text = String(cell).replace(/\r\n/g, '\n').trim();
  if (!text || /^monatskommentar\s*:?\s*$/i.test(text)) return '';
  return text;
}

/** Light render of SQUAD `**bold**` markers for the Kommentar tab. */
function renderKommentarWithBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(part);
    if (m) {
      return (
        <Box component="strong" key={i} sx={{ fontWeight: 700 }}>
          {m[1]}
        </Box>
      );
    }
    return <Box component="span" key={i}>{part}</Box>;
  });
}

export const PortfolioUploadTabs: FC<PortfolioUploadTabsProps> = ({ uploadedData }) => {
  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => setActiveTab(newValue);

  const portfolioWidgets = uploadedData?.portfolioWidgets ?? [];
  const theme = uploadedData?.theme ?? ({} as ThemeData);
  const monatskommentar = resolveMonatskommentar(uploadedData);

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="portfolio detail tabs">
          {TAB_LABELS.map((label, index) => (
            <Tab
              key={label}
              label={label}
              id={`portfolio-detail-tab-${index}`}
              aria-controls={`portfolio-detail-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab 1: Kommentar — from Excel sheet 1 cell A5 */}
      <TabPanel value={activeTab} index={0}>
        {monatskommentar ? (
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Monatskommentar
            </Typography>
            <Typography
              variant="body2"
              component="div"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, color: 'text.primary' }}
            >
              {renderKommentarWithBold(monatskommentar)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {monatskommentar.length.toLocaleString('de-DE')} characters
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No Monatskommentar found in cell A5 of the Kommentar sheet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Re-upload the Excel file if this upload was created before comment parsing was enabled.
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* Tab 2: Portfolio - Accordions per widget item */}
      <TabPanel value={activeTab} index={1}>
        {portfolioWidgets.length === 0 ? (
          <Typography color="text.secondary">No portfolio widgets parsed.</Typography>
        ) : (
          <Box>
            {portfolioWidgets.map((item, sectionIndex) => (
              <Accordion key={sectionIndex} defaultExpanded={sectionIndex === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight="medium">{item.sectionTitle || `Section ${sectionIndex + 1}`}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {Array.isArray(item.widgets) &&
                      item.widgets.map((widget, widgetIndex) => (
                        <Paper key={widgetIndex} variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="primary" gutterBottom>
                            {widget.title || `Widget ${widgetIndex + 1}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Type: {widget.art ?? '—'} · Sortierung: {widget.sortierung ?? '—'}
                          </Typography>
                          {widget.labelMaxChars != null && (
                            <Typography variant="caption" display="block">
                              Label max: {widget.labelMaxChars} · Value max: {widget.valueMaxChars ?? '—'}
                            </Typography>
                          )}
                          {Array.isArray(widget.rows) && widget.rows.length > 0 && (
                            <TableContainer sx={{ mt: 1 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Label</TableCell>
                                    <TableCell>Value</TableCell>
                                    {widget.rows.some((r) => r.labelL != null && r.labelL !== '') && (
                                      <TableCell>Label [L]</TableCell>
                                    )}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {widget.rows.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                      <TableCell>{row.label}</TableCell>
                                      <TableCell>{row.value}</TableCell>
                                      {widget.rows!.some((r) => r.labelL != null && r.labelL !== '') && (
                                        <TableCell>{row.labelL ?? ''}</TableCell>
                                      )}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Paper>
                      ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Tab 3: Config - Theme color sets */}
      <TabPanel value={activeTab} index={2}>
        <Stack spacing={3}>
          {theme.pieCharts && theme.pieCharts.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Pie chart colors
              </Typography>
              {theme.pieCharts.map((item, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                  <Typography variant="body2" fontWeight="500">
                    {item.fund}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {(item.colors || []).map((hex, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          bgcolor: hex,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                        title={hex}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {item.colors?.join(', ')}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
          {theme.barCharts && theme.barCharts.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Bar chart colors (Hauptfarbe, Sekundärfarbe)
              </Typography>
              {theme.barCharts.map((item, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                  <Typography variant="body2" fontWeight="500">
                    {item.fund}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {(item.colors || []).map((hex, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          bgcolor: hex,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                        title={hex}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {item.colors?.join(', ')}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
          {theme.fsHeader && theme.fsHeader.length > 0 && (
            <Box>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                FS-Header colors
              </Typography>
              {theme.fsHeader.map((item, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                  <Typography variant="body2" fontWeight="500">
                    {item.fund}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {(item.colors || []).map((hex, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 0.5,
                          bgcolor: hex,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                        title={hex}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {item.colors?.join(', ')}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
          {(!theme.pieCharts || theme.pieCharts.length === 0) &&
            (!theme.barCharts || theme.barCharts.length === 0) &&
            (!theme.fsHeader || theme.fsHeader.length === 0) && (
              <Typography color="text.secondary">No theme color sets parsed.</Typography>
            )}
        </Stack>
      </TabPanel>
    </Paper>
  );
};
